import { useRef } from 'react';
import {
  Alert, Box, Button, FormControlLabel, MenuItem, Stack, Switch, TextField,
  Typography,
} from '@mui/material';

import { CHARACTER_CATEGORIES, CHARACTER_SENSE } from '../map/characterPlaces';
import { themeName } from '../copy/mission';
import type { RouteTheme } from '../api/types';

/**
 * The road answers a journey starts with, before anybody opens the section.
 *
 * One of them, and it is on: on this deployment the distance answer is what
 * is wanted nearly every time, so starting on "quickest" meant most rounds
 * were planned the wrong way once and then planned again. An emphasis that
 * says otherwise still wins, and so does anyone who turns it off.
 */
export const DEFAULT_ROAD_OPTIONS: Record<string, number | boolean> = {
  shortest: true,
};

/**
 * The same answers, with anything still unanswered taken from the default.
 *
 * Wanted wherever an emphasis has just been swapped out. Clearing one takes
 * back every key it set, which would carry `shortest` off with it and leave
 * the journey quietly on quickest — the one state nobody chose. An explicit
 * `false` is a value rather than a gap, so it survives this untouched.
 */
export function withRoadDefaults(
  options: Record<string, number | boolean>,
): Record<string, number | boolean> {
  return { ...DEFAULT_ROAD_OPTIONS, ...options };
}

/**
 * Road keys that are set without anybody being asked.
 *
 * `shortest` is on for every journey and has no control on the form, so it
 * is nobody's answer. Counted, it would put a "1" beside a section nobody
 * has opened — the opposite of what that tag is for, which is to say when
 * something inside HAS been decided.
 */
const UNASKED_ROAD_KEYS = new Set(['shortest']);

/** How many of the road questions somebody has actually answered. */
export function countRoadAnswers(
  options: Record<string, number | boolean>,
): number {
  return Object.keys(options).filter((key) => !UNASKED_ROAD_KEYS.has(key)).length;
}

/**
 * How a journey travels, and what it should be like — one set of controls.
 *
 * Written once and drawn by both planners. They had drifted: the multi-day
 * screen carried its own smaller copy of the road questions, with different
 * column widths, no "Auto — prefer" to say what the emphasis answers, and
 * none of the three sliders at all. Two copies of a question is two answers
 * to keep right, and the copy nobody was looking at is the one that fell
 * behind.
 *
 * The state belongs to the form that owns it; this only draws it.
 */
export function JourneyCharacterFields({
  theme, roadOptions, onRoadOptionsChange,
  characteristics, onCharacteristicsChange, appliesTo,
  avoidCategories, onAvoidCategoriesChange,
}: {
  /** The emphasis in force, whose answers are shown beside each control. */
  theme: RouteTheme | null;
  roadOptions: Record<string, number | boolean>;
  onRoadOptionsChange: (
    next: (current: Record<string, number | boolean>) => Record<string, number | boolean>,
  ) => void;
  characteristics: Record<string, number>;
  onCharacteristicsChange: (
    next: (current: Record<string, number>) => Record<string, number>,
  ) => void;
  /**
   * Categories already avoided outright, and how to change them.
   *
   * Offered beside a characteristic that asks to stay AWAY from something,
   * because the two are the weak and the strong form of one wish and only the
   * strong one moves the router. Optional: a screen without avoidance simply
   * does not offer the upgrade.
   */
  avoidCategories?: string[];
  onAvoidCategoriesChange?: (next: string[]) => void;
  /**
   * What a change here holds for, in the operator's own words — "this
   * journey" on one day, "every day" across an operation. The only thing that
   * differs between the two screens, because it is the only thing that is
   * actually different.
   */
  appliesTo: string;
}) {
  /*
   * Whether a menu change came from a person.
   *
   * MUI fires onChange while a select is being opened by keyboard, which
   * silently set an answer nobody had chosen.
   */
  const roadMenuArmed = useRef<Record<string, boolean>>({});

  /** What the emphasis answers for one road control, in the same words. */
  const roadWordFor = (key: string): string | null => {
    const value = theme?.routingOptions?.[key as keyof RouteTheme['routingOptions']];
    if (value === undefined) return null;
    if (typeof value === 'boolean') return value ? 'on' : 'off';
    if (value <= 0.25) return value === 0 ? 'avoid' : 'discourage';

    return value >= 0.75 ? (value === 1 ? 'prefer' : 'favor') : 'no preference';
  };

  return (
    <Stack spacing={2}>
      <Typography variant="caption" color="text.secondary">
        {theme
          ? `Every control below reads what ${themeName(theme.key, theme.label)} `
            + `says. Change one and it holds for ${appliesTo} only.`
          : 'Left on Auto, the router decides. Nothing here changes which '
            + 'places are visited — only the road between them.'}
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gap: 1,
          gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
        }}
      >
        {([
          /*
           * "Residential streets" is use_residential — the ordinary streets
           * people live on, which Valhalla has no costing option for and
           * which is answered on the candidates instead.
           */
          ['use_residential', 'Residential streets'],
          ['use_tolls', 'Toll roads'],
          ['use_ferry', 'Ferries'],
          /*
           * "Main roads" — motorway, trunk and primary together, and the one
           * control here that is not the router's. It stands in for the
           * motorway control: main roads INCLUDE motorways, so the answer is
           * passed to the router as its motorway weight as well as being
           * counted on the candidates.
           */
          ['use_main_roads', 'Main roads'],
        ] as const).map(([key, label]) => (
          <TextField
            key={key}
            select
            size="small"
            label={label}
            value={roadOptions[key] === undefined ? '' : String(roadOptions[key])}
            onChange={(e) => {
              if (!roadMenuArmed.current[key]) return;
              onRoadOptionsChange((current) => {
                const next = { ...current };
                if (e.target.value === '') delete next[key];
                else next[key] = Number(e.target.value);

                return next;
              });
            }}
            /*
             * The empty value has to be DISPLAYED, or the one thing this row
             * exists to say — what the emphasis answers — is visible only
             * after opening the menu. The label is shrunk to match, since the
             * field is never blank now.
             */
            slotProps={{
              select: {
                displayEmpty: true,
                onOpen: () => { roadMenuArmed.current[key] = false; },
                MenuProps: {
                  slotProps: {
                    list: {
                      onMouseDown: () => { roadMenuArmed.current[key] = true; },
                      onKeyDown: () => { roadMenuArmed.current[key] = true; },
                    },
                  },
                },
              },
              inputLabel: { shrink: true },
            }}
          >
            {/*
              "Auto" alone was the confusion: with an emphasis chosen it does
              not mean the router decides, it means the emphasis does — and
              what the emphasis says was nowhere on screen.
            */}
            <MenuItem value="">
              {roadWordFor(key) ? `Auto — ${roadWordFor(key)}` : 'Auto'}
            </MenuItem>
            {/*
              Three answers, not five. "Discourage" and "Favour" were the
              middle of a 0-1 weight exposed as words, and the difference
              between them and their neighbours is rarely one a road shows.
            */}
            <MenuItem value="0">Avoid</MenuItem>
            <MenuItem value="1">Prefer</MenuItem>
          </TextField>
        ))}
      </Box>

      {/*
        Prefer Shortest Route has no control here.

        It is on for every journey — see DEFAULT_ROAD_OPTIONS — and is not a
        question this deployment wants put to the traveller, so the switch has
        gone rather than sitting at its only useful setting. Nothing else
        changes: the answer still travels with the plan, an emphasis can still
        overrule it, and countRoadAnswers keeps it out of the section's tally
        so an untouched form does not claim an answer nobody gave.

        The switch survives in Admin, on the emphasis editor, which is where
        this deployment's mind can still be changed.
      */}

      {/*
        What the journey should be like, measured against our own places
        rather than the router's idea of a road. Only used where there is more
        than one way round to choose between.
      */}
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          What this journey should be like
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Used when there is more than one way round. All at nought means the
          quickest.
        </Typography>
        {/*
          Quiet is not offered here.

          It asked to stay AWAY from bars, stadiums and malls, and a
          characteristic can only pick between the lines the router already
          found — so on a city whose nightlife sits along the through roads it
          left the route running past all of it and was still working as
          designed. Avoiding those categories outright is the same wish where
          Valhalla can act on it, and that is what the avoidance controls are
          for.

          The key is still read everywhere else: a saved plan or an emphasis
          that carries `quiet` is scored exactly as before.
        */}
        {([
          ['safety', 'Near help', 'Hospitals, police and fire stations'],
          ['scenic', 'Scenic', 'Parks, gardens, viewpoints and water'],
        ] as const).map(([key, label, hint]) => (
          <Box key={key} sx={{ mt: 1 }}>
            {/*
              On or off, because there was never a third answer.

              The score is a weighted AVERAGE — sum(value x weight) divided by
              sum(weight) — so with one characteristic set the weight cancels
              out entirely and every position from 0.05 to 1.00 picks the same
              route. The number only ever meant anything as a RATIO between
              two or more, and a 0.05 step promised a precision that could not
              exist: a slider nineteen of whose positions were
              indistinguishable is why this control read as broken.

              Asking at all means asking as much as the control can ask, so on
              is 1 and off is absent.
            */}
            <FormControlLabel
              control={(
                <Switch
                  size="small"
                  checked={Boolean(characteristics[key])}
                  onChange={(e) => onCharacteristicsChange((current) => {
                    const next = { ...current };
                    if (e.target.checked) next[key] = 1;
                    else delete next[key];

                    return next;
                  })}
                />
              )}
              label={(
                <Typography variant="body2">
                  {label}
                  {/*
                    Said only when an emphasis is answering for this control,
                    since that is the one state the switch cannot show: off
                    under an emphasis still measures something.
                  */}
                  {!characteristics[key] && theme?.routeCharacteristics?.[key] !== undefined && (
                    <Typography component="span" variant="caption" color="text.secondary">
                      {' '}— set by the emphasis
                    </Typography>
                  )}
                </Typography>
              )}
              sx={{ ml: 0 }}
            />
            {/*
              Its own line. Caption renders as a span, so this sat alongside
              the switch and ran into its label instead of explaining it.
            */}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {hint}
            </Typography>
            {/*
              The weak form of this wish, and the strong one beside it.

              A characteristic asking to stay AWAY only picks between the ways
              round the router already found — it cannot make the search avoid
              anything, which is why Quiet can leave a line running past every
              bar in the city and still be working correctly. Avoiding the
              categories outright is the same wish put where Valhalla can act
              on it: real polygon exclusions, and a real change of route.

              Offered rather than done, because it is a far stronger
              instruction than the control it sits under — exclusions can make
              a journey longer, or impossible.
            */}
            {CHARACTER_SENSE[key] === 'avoid'
              && Boolean(characteristics[key])
              && onAvoidCategoriesChange
              && !(CHARACTER_CATEGORIES[key] ?? []).every((c) => avoidCategories?.includes(c)) && (
                <Alert
                  severity="info"
                  variant="outlined"
                  sx={{ mt: 1, py: 0, alignItems: 'center' }}
                  action={(
                    <Button
                      size="small"
                      onClick={() => onAvoidCategoriesChange([...new Set([
                        ...(avoidCategories ?? []),
                        ...(CHARACTER_CATEGORIES[key] ?? []),
                      ])])}
                    >
                      Avoid them
                    </Button>
                  )}
                >
                  <Typography variant="caption">
                    This only picks between routes. Avoid these outright to
                    make the router go round them.
                  </Typography>
                </Alert>
              )}
            {/*
              Said on the one that collides. "Near help" and the proximity
              score beside the itinerary are both about hospitals, police and
              fire stations, and neither touches the other.
            */}
            {key === 'safety' && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', fontStyle: 'italic' }}
              >
                Picks between routes. It does not change the proximity score
                shown with the itinerary.
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    </Stack>
  );
}
